import { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Search, Clock, DollarSign, RefreshCw } from 'lucide-react';
import { AIProvider, AIModel } from '../types';
import { modelsService } from '../services/modelsService';

interface ModelSelectorProps {
  provider: AIProvider;
  selectedModel: string;
  onModelChange: (model: string) => void;
  disabled?: boolean;
  apiKey?: string; // API key for fetching models
}

// Fallback models in case API fails
const FALLBACK_MODELS: Record<AIProvider, AIModel[]> = {
  openai: [
    {
      id: 'gpt-4o',
      name: 'GPT-4o',
      provider: 'openai',
      description: 'Latest GPT-4o model',
      contextLength: 128000,
      pricing: { input: 0.0025, output: 0.01 }
    },
    {
      id: 'gpt-4o-mini',
      name: 'GPT-4o Mini',
      provider: 'openai',
      description: 'Efficient GPT-4o variant',
      contextLength: 128000,
      pricing: { input: 0.00015, output: 0.0006 }
    },
    {
      id: 'gpt-4-turbo',
      name: 'GPT-4 Turbo',
      provider: 'openai',
      description: 'GPT-4 Turbo model',
      contextLength: 128000,
      pricing: { input: 0.01, output: 0.03 }
    }
  ],
  gemini: [
    {
      id: 'gemini-1.5-pro',
      name: 'Gemini 1.5 Pro',
      provider: 'gemini',
      description: 'Most capable Gemini model',
      contextLength: 2000000,
      pricing: { input: 0.00125, output: 0.005 }
    },
    {
      id: 'gemini-1.5-flash',
      name: 'Gemini 1.5 Flash',
      provider: 'gemini',
      description: 'Fast and efficient model',
      contextLength: 1000000,
      pricing: { input: 0.000075, output: 0.0003 }
    }
  ],
  openrouter: [
    {
      id: 'anthropic/claude-3.5-sonnet',
      name: 'Claude 3.5 Sonnet',
      provider: 'openrouter',
      description: 'Anthropic\'s advanced model',
      contextLength: 200000,
      pricing: { input: 0.003, output: 0.015 }
    },
    {
      id: 'openai/gpt-4o',
      name: 'GPT-4o',
      provider: 'openrouter',
      description: 'Latest GPT-4o model',
      contextLength: 128000,
      pricing: { input: 0.0025, output: 0.01 }
    },
    {
      id: 'meta-llama/llama-3.1-70b-instruct',
      name: 'Llama 3.1 70B',
      provider: 'openrouter',
      description: 'Meta\'s large language model',
      contextLength: 131072,
      pricing: { input: 0.00088, output: 0.00088 }
    }
  ]
};

export function ModelSelector({ provider, selectedModel, onModelChange, disabled = false, apiKey }: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dynamicModels, setDynamicModels] = useState<AIModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch models when provider or apiKey changes
  useEffect(() => {
    const fetchModels = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await modelsService.getProviderModels(provider, apiKey);

        if (response.success) {
          setDynamicModels(response.data);
        } else {
          setError(response.error || 'Failed to fetch models');
          // Use fallback models on error
          setDynamicModels(FALLBACK_MODELS[provider] || []);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        // Use fallback models on error
        setDynamicModels(FALLBACK_MODELS[provider] || []);
      } finally {
        setIsLoading(false);
      }
    };

    fetchModels();
  }, [provider, apiKey]);

  // Use dynamic models if available, otherwise fallback
  const models = dynamicModels.length > 0 ? dynamicModels : (FALLBACK_MODELS[provider] || []);
  
  const filteredModels = useMemo(() => {
    if (!searchTerm) return models;
    return models.filter(model => 
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [models, searchTerm]);
  
  const selectedModelData = models.find(m => m.id === selectedModel);
  
  useEffect(() => {
    // Auto-select first model if none selected
    if (!selectedModel && models.length > 0) {
      onModelChange(models[0].id);
    }
  }, [provider, selectedModel, models, onModelChange]);
  
  const handleModelSelect = (modelId: string) => {
    onModelChange(modelId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Clear cache and fetch fresh models
      modelsService.clearCache();
      const response = await modelsService.getProviderModels(provider, apiKey);

      if (response.success) {
        setDynamicModels(response.data);
      } else {
        setError(response.error || 'Failed to refresh models');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice) || numPrice === 0) return 'Free';
    if (numPrice >= 1) return `$${numPrice.toFixed(2)}`;
    if (numPrice >= 0.01) return `$${numPrice.toFixed(3)}`;
    if (numPrice >= 0.001) return `$${numPrice.toFixed(4)}`;
    return `$${numPrice.toFixed(6)}`;
  };
  
  const formatContextLength = (length: number) => {
    if (length >= 1000) return `${(length / 1000).toFixed(0)}K`;
    return length.toString();
  };
  
  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">
          Select Model
        </label>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={disabled || isLoading}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          title="Refresh models"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            {error} (Using fallback models)
          </p>
        </div>
      )}
      
      {/* Selected Model Display */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'
        }`}
      >
        <div className="flex-1 min-w-0">
          {selectedModelData ? (
            <div className="min-w-0">
              <div className="font-medium text-gray-900 truncate">{selectedModelData.name}</div>
              <div className="text-sm text-gray-500 truncate">
                {selectedModelData.description && selectedModelData.description.length > 60
                  ? selectedModelData.description.substring(0, 60) + '...'
                  : selectedModelData.description}
              </div>
            </div>
          ) : (
            <div className="text-gray-500">Select a model...</div>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-hidden">
          {/* Search for OpenRouter */}
          {provider === 'openrouter' && (
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search models..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
          
          {/* Model List */}
          <div className="max-h-80 overflow-y-auto">
            {filteredModels.length === 0 ? (
              <div className="p-3 text-gray-500 text-center">No models found</div>
            ) : (
              filteredModels.map((model) => (
                <button
                  key={model.id}
                  onClick={() => handleModelSelect(model.id)}
                  className={`w-full px-3 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                    selectedModel === model.id ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{model.name}</div>
                      <div className="text-sm text-gray-600 mt-1 truncate">
                        {model.description && model.description.length > 80
                          ? model.description.substring(0, 80) + '...'
                          : model.description}
                      </div>
                      
                      {/* Model Stats */}
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        {model.contextLength && (
                          <div className="flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatContextLength(model.contextLength)} tokens
                          </div>
                        )}
                        {model.pricing && (
                          <div className="flex items-center">
                            <DollarSign className="h-3 w-3 mr-1" />
                            {formatPrice(model.pricing.input)}/1K in
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {selectedModel === model.id && (
                      <div className="ml-2 flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
